import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, ChevronLeft, Sparkles, Plus, PieChart, Target, 
  Bell, CheckCircle, Users, Wallet, PiggyBank, Split, Share2, User,
  RefreshCw, FileText, BarChart3, Calendar, Upload, ArrowRight, Settings, Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getFamily } from '../lib/api';

// Tutorial page sequence for chaining
const TUTORIAL_SEQUENCE = ['dashboard', 'transactions', 'budgets', 'recurring', 'objectives', 'invoices', 'settings'];

// Tutorial steps organized by page
const TUTORIAL_STEPS = {
  // Dashboard steps
  dashboard: [
    {
      id: 'welcome',
      icon: Sparkles,
      title_fr: 'Bienvenue sur SpenSpot !',
      title_en: 'Welcome to SpenSpot!',
      title_pt: 'Bem-vindo ao SpenSpot!',
      description_fr: 'Votre assistant finances personnelles. Laissez-moi vous faire découvrir les fonctionnalités clés.',
      description_en: 'Your personal finance assistant. Let me show you the key features.',
      description_pt: 'Seu assistente de finanças pessoais. Deixe-me mostrar os recursos principais.',
      target: null,
      position: 'center',
    },
    {
      id: 'balance-card',
      icon: Wallet,
      title_fr: 'Votre solde disponible',
      title_en: 'Your available balance',
      title_pt: 'Seu saldo disponível',
      description_fr: 'Ici vous voyez votre solde en temps réel. Cliquez sur l\'œil pour masquer les montants.',
      description_en: 'Here you can see your real-time balance. Click the eye to hide amounts.',
      description_pt: 'Aqui você pode ver seu saldo em tempo real. Clique no olho para ocultar valores.',
      target: '[data-testid="balance-card"], [data-testid="pot-commun-card"]',
      position: 'bottom',
    },
    {
      id: 'fab-button',
      icon: Plus,
      title_fr: 'Ajoutez une dépense rapidement',
      title_en: 'Quickly add an expense',
      title_pt: 'Adicione uma despesa rapidamente',
      description_fr: 'Ce bouton orange vous permet d\'ajouter une dépense en 1 clic. Vous pouvez aussi scanner un ticket ou utiliser la voix !',
      description_en: 'This orange button lets you add an expense in 1 click. You can also scan a receipt or use voice!',
      description_pt: 'Este botão laranja permite adicionar uma despesa em 1 clique. Você também pode escanear um recibo ou usar a voz!',
      target: '[data-testid="fab-toggle"]',
      position: 'left',
    },
    {
      id: 'budget-progress',
      icon: Target,
      title_fr: 'Suivez vos budgets',
      title_en: 'Track your budgets',
      title_pt: 'Acompanhe seus orçamentos',
      description_fr: 'Définissez des limites par catégorie et suivez votre progression. Recevez des alertes avant de dépasser !',
      description_en: 'Set limits by category and track your progress. Get alerts before you exceed them!',
      description_pt: 'Defina limites por categoria e acompanhe seu progresso. Receba alertas antes de excedê-los!',
      target: '[data-testid="budget-card"]',
      position: 'top',
    },
    {
      id: 'tools-section',
      icon: PieChart,
      title_fr: 'Outils puissants',
      title_en: 'Powerful tools',
      title_pt: 'Ferramentas poderosas',
      description_fr: 'Graphiques, rapports mensuels, export fiscal, prévisions IA... Explorez tous les outils pour mieux gérer vos finances.',
      description_en: 'Charts, monthly reports, tax export, AI predictions... Explore all tools to better manage your finances.',
      description_pt: 'Gráficos, relatórios mensais, exportação fiscal, previsões de IA... Explore todas as ferramentas para gerenciar melhor suas finanças.',
      target: '[data-testid="analytics-tool"]',
      position: 'top',
    },
  ],
  
  // Transactions page steps
  transactions: [
    {
      id: 'txn-welcome',
      icon: Calendar,
      title_fr: 'Vos transactions',
      title_en: 'Your transactions',
      title_pt: 'Suas transações',
      description_fr: 'Retrouvez ici l\'historique complet de vos dépenses, triées par date.',
      description_en: 'Find here the complete history of your expenses, sorted by date.',
      description_pt: 'Encontre aqui o histórico completo de suas despesas, ordenadas por data.',
      target: null,
      position: 'center',
    },
    {
      id: 'txn-search',
      icon: FileText,
      title_fr: 'Recherchez et filtrez',
      title_en: 'Search and filter',
      title_pt: 'Pesquise e filtre',
      description_fr: 'Utilisez la recherche et les filtres pour trouver rapidement une dépense spécifique.',
      description_en: 'Use search and filters to quickly find a specific expense.',
      description_pt: 'Use a pesquisa e os filtros para encontrar rapidamente uma despesa específica.',
      target: '[data-testid="search-input"]',
      position: 'bottom',
    },
    {
      id: 'txn-import',
      icon: Upload,
      title_fr: 'Importez vos relevés',
      title_en: 'Import your statements',
      title_pt: 'Importe seus extratos',
      description_fr: 'Importez vos relevés bancaires ou fichiers CSV pour ajouter plusieurs transactions en une fois.',
      description_en: 'Import your bank statements or CSV files to add multiple transactions at once.',
      description_pt: 'Importe seus extratos bancários ou arquivos CSV para adicionar várias transações de uma vez.',
      target: '[data-testid="import-btn"], [data-testid="import-btn-mobile"]',
      position: 'bottom',
    },
  ],
  
  // Budgets page steps
  budgets: [
    {
      id: 'budgets-welcome',
      icon: PiggyBank,
      title_fr: 'Gérez vos budgets',
      title_en: 'Manage your budgets',
      title_pt: 'Gerir orçamentos',
      description_fr: 'Définissez des limites de dépenses par catégorie pour mieux contrôler vos finances.',
      description_en: 'Set spending limits by category to better control your finances.',
      description_pt: 'Defina limites de gastos por categoria para controlar melhor as suas finanças.',
      target: null,
      position: 'center',
    },
    {
      id: 'budgets-add',
      icon: Plus,
      title_fr: 'Créer un budget',
      title_en: 'Create a budget',
      title_pt: 'Criar um orçamento',
      description_fr: 'Ajoutez un budget pour chaque catégorie de dépenses que vous souhaitez surveiller.',
      description_en: 'Add a budget for each expense category you want to monitor.',
      description_pt: 'Adicione um orçamento para cada categoria de despesas que deseja monitorar.',
      target: '[data-testid="add-budget-btn"]',
      position: 'bottom',
    },
    {
      id: 'budgets-progress',
      icon: Target,
      title_fr: 'Suivez votre progression',
      title_en: 'Track your progress',
      title_pt: 'Acompanhe seu progresso',
      description_fr: 'Visualisez en un coup d\'œil combien vous avez dépensé par rapport à vos limites.',
      description_en: 'See at a glance how much you\'ve spent compared to your limits.',
      description_pt: 'Veja rapidamente quanto gastou em relação aos seus limites.',
      target: '[data-testid="overall-budget-card"]',
      position: 'bottom',
    },
  ],
  
  // Recurring/Subscriptions page steps
  recurring: [
    {
      id: 'recurring-welcome',
      icon: RefreshCw,
      title_fr: 'Paiements récurrents',
      title_en: 'Recurring payments',
      title_pt: 'Pagamentos recorrentes',
      description_fr: 'Gérez vos abonnements et dépenses récurrentes. L\'app détecte automatiquement vos habitudes !',
      description_en: 'Manage your subscriptions and recurring expenses. The app automatically detects your patterns!',
      description_pt: 'Gerencie suas assinaturas e despesas recorrentes. O app detecta automaticamente seus padrões!',
      target: null,
      position: 'center',
    },
    {
      id: 'recurring-detect',
      icon: Sparkles,
      title_fr: 'Détection automatique',
      title_en: 'Automatic detection',
      title_pt: 'Detecção automática',
      description_fr: 'SpenSpot analyse vos transactions et vous suggère les dépenses récurrentes à confirmer.',
      description_en: 'SpenSpot analyzes your transactions and suggests recurring expenses to confirm.',
      description_pt: 'SpenSpot analisa suas transações e sugere despesas recorrentes para confirmar.',
      target: '[data-testid="detect-recurring-btn"]',
      position: 'bottom',
    },
  ],
  
  // Objectives page steps
  objectives: [
    {
      id: 'obj-welcome',
      icon: Target,
      title_fr: 'Vos objectifs d\'épargne',
      title_en: 'Your savings goals',
      title_pt: 'Suas metas de economia',
      description_fr: 'Créez des objectifs pour économiser vers ce qui compte pour vous : vacances, voiture, maison...',
      description_en: 'Create goals to save towards what matters to you: vacation, car, house...',
      description_pt: 'Crie metas para economizar para o que importa para você: férias, carro, casa...',
      target: null,
      position: 'center',
    },
    {
      id: 'obj-create',
      icon: Plus,
      title_fr: 'Créez un objectif',
      title_en: 'Create a goal',
      title_pt: 'Crie uma meta',
      description_fr: 'Définissez un montant cible et une date. L\'app calculera combien économiser chaque mois.',
      description_en: 'Set a target amount and date. The app will calculate how much to save each month.',
      description_pt: 'Defina um valor alvo e uma data. O app calculará quanto economizar por mês.',
      target: '[data-testid="create-objective-btn"]',
      position: 'bottom',
    },
    {
      id: 'obj-auto',
      icon: Sparkles,
      title_fr: 'Épargne automatique',
      title_en: 'Automatic savings',
      title_pt: 'Poupança automática',
      description_fr: 'Activez l\'épargne automatique pour contribuer régulièrement sans y penser.',
      description_en: 'Enable automatic savings to contribute regularly without thinking about it.',
      description_pt: 'Ative a poupança automática para contribuir regularmente sem pensar nisso.',
      target: null,
      position: 'center',
    },
  ],
  
  // Invoices page steps
  invoices: [
    {
      id: 'invoices-welcome',
      icon: FileText,
      title_fr: 'Vos factures et garanties',
      title_en: 'Your invoices and warranties',
      title_pt: 'Suas faturas e garantias',
      description_fr: 'Stockez vos factures et suivez les garanties de vos achats importants.',
      description_en: 'Store your invoices and track warranties for your important purchases.',
      description_pt: 'Armazene suas faturas e acompanhe as garantias de suas compras importantes.',
      target: null,
      position: 'center',
    },
    {
      id: 'invoices-upload',
      icon: Upload,
      title_fr: 'Téléchargez une facture',
      title_en: 'Upload an invoice',
      title_pt: 'Carregue uma fatura',
      description_fr: 'Ajoutez vos factures en photo ou PDF. L\'OCR extrait automatiquement les informations !',
      description_en: 'Add your invoices as photos or PDFs. OCR automatically extracts the information!',
      description_pt: 'Adicione suas faturas como fotos ou PDFs. O OCR extrai automaticamente as informações!',
      target: '[data-testid="upload-invoice-btn"]',
      position: 'bottom',
    },
    {
      id: 'invoices-warranty',
      icon: Shield,
      title_fr: 'Alertes de garantie',
      title_en: 'Warranty alerts',
      title_pt: 'Alertas de garantia',
      description_fr: 'Recevez des notifications avant l\'expiration de vos garanties.',
      description_en: 'Get notifications before your warranties expire.',
      description_pt: 'Receba notificações antes de suas garantias expirarem.',
      target: null,
      position: 'center',
    },
  ],
  
  // Settings page steps
  settings: [
    {
      id: 'settings-welcome',
      icon: Settings,
      title_fr: 'Personnalisez SpenSpot',
      title_en: 'Customize SpenSpot',
      title_pt: 'Personalize o SpenSpot',
      description_fr: 'Configurez l\'application selon vos préférences : langue, thème, notifications...',
      description_en: 'Configure the app according to your preferences: language, theme, notifications...',
      description_pt: 'Configure o app de acordo com suas preferências: idioma, tema, notificações...',
      target: null,
      position: 'center',
    },
    {
      id: 'settings-personality',
      icon: Sparkles,
      title_fr: 'Personnalité de l\'IA',
      title_en: 'AI Personality',
      title_pt: 'Personalidade da IA',
      description_fr: 'Choisissez le style de votre assistant IA : Pote, Coach, Bienveillant...',
      description_en: 'Choose your AI assistant\'s style: Buddy, Coach, Caring...',
      description_pt: 'Escolha o estilo do seu assistente IA: Amigo, Treinador, Carinhoso...',
      target: '[data-testid="personality-card"]',
      position: 'bottom',
    },
    {
      id: 'settings-end',
      icon: CheckCircle,
      title_fr: 'Tutoriel terminé !',
      title_en: 'Tutorial complete!',
      title_pt: 'Tutorial completo!',
      description_fr: 'Vous êtes prêt à gérer vos finances comme un pro ! N\'hésitez pas à explorer toutes les fonctionnalités.',
      description_en: 'You\'re ready to manage your finances like a pro! Feel free to explore all the features.',
      description_pt: 'Você está pronto para gerenciar suas finanças como um profissional! Sinta-se à vontade para explorar todos os recursos.',
      target: null,
      position: 'center',
    },
  ],
  
  // Family page steps
  family: [
    {
      id: 'family-welcome',
      icon: Users,
      title_fr: 'Gestion familiale',
      title_en: 'Family management',
      title_pt: 'Gestão familiar',
      description_fr: 'Gérez vos finances en famille ! Partagez les dépenses, suivez les contributions de chacun.',
      description_en: 'Manage finances as a family! Share expenses, track everyone\'s contributions.',
      description_pt: 'Gerencie finanças em família! Compartilhe despesas, acompanhe as contribuições de todos.',
      target: null,
      position: 'center',
    },
    {
      id: 'family-invite',
      icon: Share2,
      title_fr: 'Invitez votre famille',
      title_en: 'Invite your family',
      title_pt: 'Convide sua família',
      description_fr: 'Partagez le code d\'invitation avec votre conjoint(e) ou vos enfants pour gérer ensemble.',
      description_en: 'Share the invitation code with your spouse or children to manage together.',
      description_pt: 'Compartilhe o código de convite com seu cônjuge ou filhos para gerenciar juntos.',
      target: '[data-testid="invite-member-btn"]',
      position: 'bottom',
    },
    {
      id: 'family-mode',
      icon: Split,
      title_fr: 'Mode budget',
      title_en: 'Budget mode',
      title_pt: 'Modo de orçamento',
      description_fr: 'Choisissez entre budget partagé, séparé ou hybride (pot commun) selon vos préférences.',
      description_en: 'Choose between shared, separate or hybrid (common pot) budget based on your preferences.',
      description_pt: 'Escolha entre orçamento compartilhado, separado ou híbrido (pote comum) com base em suas preferências.',
      target: '[data-testid="budget-mode-selector"]',
      position: 'top',
    },
  ],
};

// Get page key from pathname
function getPageKey(pathname) {
  if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
  if (pathname === '/transactions') return 'transactions';
  if (pathname === '/budgets') return 'budgets';
  if (pathname === '/recurring' || pathname === '/subscriptions') return 'recurring';
  if (pathname === '/objectives') return 'objectives';
  if (pathname === '/invoices') return 'invoices';
  if (pathname === '/settings') return 'settings';
  if (pathname === '/family') return 'family';
  return null;
}

// Get route from page key
function getRouteFromPageKey(pageKey) {
  const routes = {
    dashboard: '/dashboard',
    transactions: '/transactions',
    budgets: '/budgets',
    recurring: '/recurring',
    objectives: '/objectives',
    invoices: '/invoices',
    settings: '/settings',
    family: '/family',
  };
  return routes[pageKey] || '/dashboard';
}

// Calculate modal position to not cover the target element
function calculateModalPosition(targetRect, windowWidth, windowHeight, preferredPosition) {
  const isMobile = windowWidth < 640;
  const padding = isMobile ? 16 : 16;
  const modalWidth = Math.min(isMobile ? windowWidth - (padding * 2) : 400, windowWidth - 32);
  const modalHeight = 280; // Approximate modal height
  const arrowOffset = 12;
  const bottomNavHeight = isMobile ? 80 : 0; // Account for bottom navigation on mobile

  if (!targetRect) {
    // Center position when no target - use margins instead of transform
    const leftPos = Math.max(padding, (windowWidth - modalWidth) / 2);
    return {
      style: {
        position: 'fixed',
        top: '50%',
        left: `${leftPos}px`,
        transform: 'translateY(-50%)',
        width: `${modalWidth}px`,
        maxWidth: `calc(100% - ${padding * 2}px)`,
      },
      position: 'center',
    };
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  // Calculate available space in each direction (accounting for bottom nav)
  const spaceTop = targetRect.top - padding;
  const spaceBottom = windowHeight - targetRect.bottom - padding - bottomNavHeight;
  const spaceLeft = targetRect.left - padding;
  const spaceRight = windowWidth - targetRect.right - padding;

  // Determine best position
  let position = preferredPosition;
  
  // On mobile, prefer top position if bottom doesn't have enough space
  if (isMobile && position !== 'center') {
    if (spaceBottom < modalHeight && spaceTop >= modalHeight) {
      position = 'top';
    } else if (spaceTop < modalHeight && spaceBottom >= modalHeight) {
      position = 'bottom';
    } else if (spaceTop >= spaceBottom) {
      position = 'top';
    } else {
      position = 'bottom';
    }
  }
  
  // Auto-adjust if preferred position doesn't have enough space
  if (position === 'bottom' && spaceBottom < modalHeight) {
    position = spaceTop >= modalHeight ? 'top' : (spaceTop > spaceBottom ? 'top' : 'bottom');
  } else if (position === 'top' && spaceTop < modalHeight) {
    position = spaceBottom >= modalHeight ? 'bottom' : (spaceBottom > spaceTop ? 'bottom' : 'top');
  } else if (position === 'left' && spaceLeft < modalWidth) {
    position = spaceRight > spaceLeft ? 'right' : 'bottom';
  } else if (position === 'right' && spaceRight < modalWidth) {
    position = spaceLeft > spaceRight ? 'left' : 'bottom';
  }

  let style = {
    position: 'fixed',
    maxWidth: `${modalWidth}px`,
    width: `calc(100% - ${padding * 2}px)`,
  };

  switch (position) {
    case 'top':
      // Position modal above the target
      const topBottomValue = windowHeight - targetRect.top + arrowOffset;
      // Ensure modal is visible within viewport
      style.bottom = `${Math.max(bottomNavHeight + padding, topBottomValue)}px`;
      style.left = `${Math.max(padding, Math.min(targetCenterX - modalWidth / 2, windowWidth - modalWidth - padding))}px`;
      break;
    case 'bottom':
      // Position modal below the target
      let bottomTopValue = targetRect.bottom + arrowOffset;
      // Check if modal would overflow bottom of screen
      const maxTop = windowHeight - modalHeight - bottomNavHeight - padding;
      if (bottomTopValue > maxTop) {
        // Reposition to be visible
        bottomTopValue = Math.min(bottomTopValue, maxTop);
      }
      style.top = `${Math.max(padding, bottomTopValue)}px`;
      style.left = `${Math.max(padding, Math.min(targetCenterX - modalWidth / 2, windowWidth - modalWidth - padding))}px`;
      break;
    case 'left':
      style.right = `${windowWidth - targetRect.left + arrowOffset}px`;
      style.top = `${Math.max(padding, Math.min(targetCenterY - modalHeight / 2, windowHeight - modalHeight - bottomNavHeight - padding))}px`;
      break;
    case 'right':
      style.left = `${targetRect.right + arrowOffset}px`;
      style.top = `${Math.max(padding, Math.min(targetCenterY - modalHeight / 2, windowHeight - modalHeight - bottomNavHeight - padding))}px`;
      break;
    default:
      // Center fallback
      style.top = '50%';
      style.left = '50%';
      style.transform = 'translate(-50%, -50%)';
  }

  return { style, position };
}

export default function OnboardingTutorial({ onComplete }) {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [tutorialSteps, setTutorialSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalPosition, setModalPosition] = useState({ style: {}, position: 'center' });
  const [hasFamily, setHasFamily] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  
  const resizeObserverRef = useRef(null);

  // Initialize tutorial based on current page
  useEffect(() => {
    const initTutorial = async () => {
      // Check if tutorial already completed for this page
      const completedPages = JSON.parse(localStorage.getItem('spenspot-tutorial-completed') || '{}');
      const pageKey = getPageKey(location.pathname);
      
      if (!pageKey || completedPages[pageKey]) {
        setIsVisible(false);
        onComplete?.();
        return;
      }

      setCurrentPage(pageKey);

      // Check if user has a family (for family-specific steps)
      if (user?.family_id) {
        try {
          const res = await getFamily();
          if (res.data?.family) {
            setHasFamily(true);
          }
        } catch (e) {
          console.log('No family found');
        }
      }
      
      // Get steps for current page
      let steps = TUTORIAL_STEPS[pageKey] || [];
      
      // Filter family steps if user doesn't have a family subscription
      if (pageKey === 'family' && !user?.family_id) {
        // Show only the welcome step for users without family
        steps = steps.slice(0, 1);
      }
      
      setTutorialSteps(steps);
      setCurrentStep(0);
      setLoading(false);
    };

    initTutorial();
  }, [location.pathname, user, onComplete]);

  // Get current step
  const step = tutorialSteps[currentStep];
  const Icon = step?.icon;

  // Update target highlight and modal position
  const updateTargetPosition = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      setModalPosition(calculateModalPosition(null, window.innerWidth, window.innerHeight, 'center'));
      return;
    }

    // Try to find the target element
    const element = document.querySelector(step.target);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      
      // Calculate modal position
      const { style, position } = calculateModalPosition(
        rect, 
        window.innerWidth, 
        window.innerHeight, 
        step.position || 'bottom'
      );
      setModalPosition({ style, position });
      
      // Scroll element into view if needed
      if (rect.top < 100 || rect.bottom > window.innerHeight - 100) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setTargetRect(null);
      setModalPosition(calculateModalPosition(null, window.innerWidth, window.innerHeight, 'center'));
    }
  }, [step]);

  // Watch for target element and window resize
  useEffect(() => {
    updateTargetPosition();
    
    // Observe for DOM changes (in case element appears later)
    const observer = new MutationObserver(() => {
      updateTargetPosition();
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Listen for window resize
    const handleResize = () => updateTargetPosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [currentStep, step?.target, updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Mark this page's tutorial as completed
    const completedPages = JSON.parse(localStorage.getItem('spenspot-tutorial-completed') || '{}');
    completedPages[currentPage] = true;
    localStorage.setItem('spenspot-tutorial-completed', JSON.stringify(completedPages));
    
    // Find the next page in sequence that hasn't been completed
    const currentIndex = TUTORIAL_SEQUENCE.indexOf(currentPage);
    let nextPageKey = null;
    
    for (let i = currentIndex + 1; i < TUTORIAL_SEQUENCE.length; i++) {
      const pageKey = TUTORIAL_SEQUENCE[i];
      if (!completedPages[pageKey]) {
        nextPageKey = pageKey;
        break;
      }
    }
    
    setIsVisible(false);
    
    // If there's a next page, navigate to it after a short delay
    if (nextPageKey) {
      setTimeout(() => {
        navigate(getRouteFromPageKey(nextPageKey));
      }, 300);
    }
    
    onComplete?.();
  };

  const handleSkip = () => {
    // Mark ALL tutorial pages as completed when user skips
    const completedPages = {};
    TUTORIAL_SEQUENCE.forEach(pageKey => {
      completedPages[pageKey] = true;
    });
    localStorage.setItem('spenspot-tutorial-completed', JSON.stringify(completedPages));
    
    setIsVisible(false);
    onComplete?.();
  };

  // Get text based on language
  const getText = (key) => {
    if (!step) return '';
    const langKey = `${key}_${language}`;
    return step[langKey] || step[`${key}_en`] || '';
  };
  
  // Check if there's a next page in the tutorial sequence
  const hasNextPage = () => {
    const completedPages = JSON.parse(localStorage.getItem('spenspot-tutorial-completed') || '{}');
    const currentIndex = TUTORIAL_SEQUENCE.indexOf(currentPage);
    
    for (let i = currentIndex + 1; i < TUTORIAL_SEQUENCE.length; i++) {
      const pageKey = TUTORIAL_SEQUENCE[i];
      if (!completedPages[pageKey]) {
        return true;
      }
    }
    return false;
  };
  
  // Get button text for the last step
  const getLastStepButtonText = () => {
    if (hasNextPage()) {
      return language === 'fr' ? 'Page suivante' : language === 'pt' ? 'Próxima página' : 'Next page';
    }
    return t('finish');
  };

  if (!isVisible || loading || !step) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] pointer-events-none"
        data-testid="onboarding-tutorial"
      >
        {/* Spotlight overlay with cutout for target element */}
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-auto"
          onClick={handleSkip}
        >
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect 
            x="0" 
            y="0" 
            width="100%" 
            height="100%" 
            fill="rgba(0, 0, 0, 0.7)" 
            mask="url(#spotlight-mask)"
          />
        </svg>

        {/* Highlight ring around target element */}
        {targetRect && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute pointer-events-none"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
            }}
          >
            <div className="w-full h-full rounded-xl ring-4 ring-primary ring-offset-2 ring-offset-transparent animate-pulse" />
          </motion.div>
        )}

        {/* Tutorial Card - Positioned dynamically */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-4 sm:p-5 pointer-events-auto z-[102] mx-3"
          style={modalPosition.style}
          data-testid="tutorial-modal"
        >
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 text-muted-foreground hover:text-foreground rounded-full hover:bg-muted transition-colors"
            data-testid="tutorial-close-btn"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="pr-5 sm:pr-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                {Icon && <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-bold mb-1 leading-tight break-words">
                  {getText('title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {getText('description')}
                </p>
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {tutorialSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'bg-primary w-6' 
                    : idx < currentStep 
                      ? 'bg-primary/50 hover:bg-primary/70' 
                      : 'bg-muted hover:bg-muted-foreground/30'
                }`}
                data-testid={`tutorial-dot-${idx}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="gap-1"
              data-testid="tutorial-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('back')}
            </Button>

            <span className="text-xs text-muted-foreground">
              {currentStep + 1} / {tutorialSteps.length}
            </span>

            <Button 
              size="sm" 
              onClick={handleNext} 
              className="gap-1"
              data-testid="tutorial-next-btn"
            >
              {currentStep === tutorialSteps.length - 1 ? (
                <>
                  {hasNextPage() ? (
                    <ArrowRight className="w-4 h-4" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {getLastStepButtonText()}
                </>
              ) : (
                <>
                  {t('next')}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleSkip}
            className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="tutorial-skip-btn"
          >
            {language === 'fr' ? 'Passer le tutoriel' : language === 'pt' ? 'Pular o tutorial' : 'Skip tutorial'}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to check if tutorial should show for current page
export function useTutorial() {
  const location = useLocation();
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const completedPages = JSON.parse(localStorage.getItem('spenspot-tutorial-completed') || '{}');
    const pageKey = getPageKey(location.pathname);
    
    // Show tutorial if this page hasn't been completed yet
    setShouldShow(pageKey && !completedPages[pageKey]);
  }, [location.pathname]);

  const resetTutorial = (page = null) => {
    if (page) {
      // Reset specific page
      const completedPages = JSON.parse(localStorage.getItem('spenspot-tutorial-completed') || '{}');
      delete completedPages[page];
      localStorage.setItem('spenspot-tutorial-completed', JSON.stringify(completedPages));
    } else {
      // Reset all tutorials
      localStorage.removeItem('spenspot-tutorial-completed');
    }
    setShouldShow(true);
  };

  const resetAllTutorials = () => {
    localStorage.removeItem('spenspot-tutorial-completed');
    setShouldShow(true);
  };

  return { shouldShow, resetTutorial, resetAllTutorials };
}
